import * as z from "zod";

/** Self-serve cutoff: below 1k orders in the last 30 days. */
export const RETURNS_LARGE_MERCHANT_MONTHLY_ORDER_THRESHOLD = 1000;

/** Annual volume cutoff (monthly threshold × 12). */
export const RETURNS_LARGE_MERCHANT_THRESHOLD =
  RETURNS_LARGE_MERCHANT_MONTHLY_ORDER_THRESHOLD * 12;

/** `form_data` key under which the persisted routing decision is stored. */
export const RETURNS_ROUTING_DECISION_STEP_ID = "routing-decision";

export const returnsRoutingClassificationSchema = z.discriminatedUnion(
  "reason",
  [
    z.object({
      decision: z.literal("call"),
      reason: z.literal("volume-at-or-above-threshold"),
      needsReview: z.literal(false).optional(),
    }),
    z.object({
      decision: z.literal("self-serve"),
      reason: z.literal("volume-below-threshold"),
      needsReview: z.literal(false).optional(),
    }),
    z.object({
      decision: z.literal("call"),
      reason: z.literal("provisional-volume-at-or-above-threshold"),
      needsReview: z.literal(false).optional(),
    }),
    z.object({
      decision: z.literal("self-serve"),
      reason: z.literal("provisional-volume-below-threshold"),
      needsReview: z.literal(false).optional(),
    }),
    z.object({
      decision: z.literal("self-serve"),
      reason: z.literal("volume-unavailable-defaulted-small"),
      needsReview: z.literal(true),
    }),
    // Historical decisions written before missing volume defaulted to self-serve.
    z.object({
      decision: z.literal("call"),
      reason: z.literal("volume-unavailable-after-polling"),
      needsReview: z.literal(false).optional(),
    }),
  ],
);

export type ReturnsRoutingClassification = z.infer<
  typeof returnsRoutingClassificationSchema
>;

/** Routes large merchants to call; missing volume defaults to self-serve + review. */
export function classifyReturnsRouting(
  totalAnnualOrderVolume: number | null,
  provisionalOrderCount30d: number | null,
): ReturnsRoutingClassification {
  if (totalAnnualOrderVolume != null) {
    return totalAnnualOrderVolume >= RETURNS_LARGE_MERCHANT_THRESHOLD
      ? { decision: "call", reason: "volume-at-or-above-threshold" }
      : { decision: "self-serve", reason: "volume-below-threshold" };
  }
  if (provisionalOrderCount30d != null) {
    return provisionalOrderCount30d >=
      RETURNS_LARGE_MERCHANT_MONTHLY_ORDER_THRESHOLD
      ? { decision: "call", reason: "provisional-volume-at-or-above-threshold" }
      : {
          decision: "self-serve",
          reason: "provisional-volume-below-threshold",
        };
  }
  return {
    decision: "self-serve",
    reason: "volume-unavailable-defaulted-small",
    needsReview: true,
  };
}
